/**
 * Documents React Query Hooks
 *
 * Provides React Query hooks for document instance data fetching and
 * mutations (Content control / custom XML sync, Part 1 - web app half).
 * Includes cache invalidation matching the templates/content-hub hook
 * patterns used elsewhere in the admin app.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query'
import {
  documentService,
  type DocumentInstanceDetailDto,
  type DocumentsODataResponse,
  type DocumentFieldDto,
  type CreateDocumentRequest,
  type UpdateDocumentFieldRequest,
} from '@/lib/api/services/documents'
import type { ODataQueryParams } from '@/lib/api/odata/query-builder'

// ============================================
// Query Keys
// ============================================

export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (params?: ODataQueryParams) => [...documentKeys.lists(), params] as const,
  details: () => [...documentKeys.all, 'detail'] as const,
  detail: (id: string) => [...documentKeys.details(), id] as const,
  fields: (id: string) => [...documentKeys.all, 'fields', id] as const,
}

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to fetch document instances with OData query support.
 */
export function useDocuments(
  params?: ODataQueryParams,
  options?: Omit<UseQueryOptions<DocumentsODataResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: documentKeys.list(params),
    queryFn: () => documentService.getDocuments(params),
    ...options,
  })
}

/**
 * Hook to fetch a single document instance with its fields.
 */
export function useDocument(
  id: string,
  options?: Omit<UseQueryOptions<DocumentInstanceDetailDto, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: documentKeys.detail(id),
    queryFn: () => documentService.getDocument(id),
    enabled: !!id,
    ...options,
  })
}

/**
 * Hook to fetch just the fields of a document instance.
 */
export function useDocumentFields(
  id: string,
  options?: Omit<UseQueryOptions<DocumentFieldDto[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: documentKeys.fields(id),
    queryFn: () => documentService.getFields(id),
    enabled: !!id,
    ...options,
  })
}

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to create a new document instance from a template.
 */
export function useCreateDocument(
  options?: Omit<UseMutationOptions<DocumentInstanceDetailDto, Error, CreateDocumentRequest>, 'mutationFn'>
) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: (data: CreateDocumentRequest) => documentService.createDocument(data),
    onSuccess: (newDocument, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() })
      options?.onSuccess?.(newDocument, variables, onMutateResult, context)
    },
  })
}

/**
 * Hook to archive a document instance.
 */
export function useArchiveDocument(
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>
) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: (id: string) => documentService.archiveDocument(id),
    onSuccess: (data, id, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() })
      queryClient.invalidateQueries({ queryKey: documentKeys.detail(id) })
      options?.onSuccess?.(data, id, onMutateResult, context)
    },
  })
}

type UpdateFieldVars = { documentId: string; fieldId: string; data: UpdateDocumentFieldRequest }

/**
 * Hook to update a field's value with optimistic concurrency
 * (`expectedVersion`). Rejects with `DocumentFieldConflictError` on a 409.
 */
export function useUpdateDocumentField(
  options?: Omit<UseMutationOptions<DocumentFieldDto, Error, UpdateFieldVars>, 'mutationFn'>
) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: ({ documentId, fieldId, data }: UpdateFieldVars) =>
      documentService.updateField(documentId, fieldId, data),
    onSuccess: (updatedField, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.detail(variables.documentId) })
      queryClient.invalidateQueries({ queryKey: documentKeys.fields(variables.documentId) })
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() })
      options?.onSuccess?.(updatedField, variables, onMutateResult, context)
    },
  })
}

type FieldActionVars = { documentId: string; fieldId: string }

/**
 * Hook to confirm deletion of a PendingDelete field.
 */
export function useConfirmDeleteField(
  options?: Omit<UseMutationOptions<void, Error, FieldActionVars>, 'mutationFn'>
) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: ({ documentId, fieldId }: FieldActionVars) =>
      documentService.confirmDeleteField(documentId, fieldId),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.detail(variables.documentId) })
      queryClient.invalidateQueries({ queryKey: documentKeys.fields(variables.documentId) })
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

/**
 * Hook to restore a PendingDelete field back to Active.
 */
export function useRestoreField(
  options?: Omit<UseMutationOptions<DocumentFieldDto, Error, FieldActionVars>, 'mutationFn'>
) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: ({ documentId, fieldId }: FieldActionVars) =>
      documentService.restoreField(documentId, fieldId),
    onSuccess: (updatedField, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.detail(variables.documentId) })
      queryClient.invalidateQueries({ queryKey: documentKeys.fields(variables.documentId) })
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() })
      options?.onSuccess?.(updatedField, variables, onMutateResult, context)
    },
  })
}
